import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { EMPTY, Observable, firstValueFrom, from, merge, of, timer } from "rxjs";
import { distinctUntilChanged, filter, map, retry, switchMap, take } from "rxjs/operators";
import { AppSettingsService } from "../../shared/app-settings-service/app-settings.service";
import { AuthService } from "../../shared/auth/auth.service";
import { DashboardsService } from "../../shared/dashboards-service/dashboards.service";
import { ResponseErrorHandlerService } from "../../shared/response-error-handler-service/response-error-handler.service";
import { NegotiateResponse } from "./negotiate-response.interface";

const RETRY_FAST_DELAY_MS = 5_000;
const RETRY_SLOW_DELAY_MS = 60_000;
const RETRY_FAST_COUNT = 3;

@Injectable({
    providedIn: "root",
})
export class SignalRService {
    private readonly http = inject(HttpClient);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    connection: HubConnection | null = null;
    readonly signalRConnected = signal<boolean>(false);
    firstToken: string | null;

    constructor() {
        merge(
            // Bootstrap: resolve current dashboard when session is ready
            this.authService.sessionInitializedEvent$.pipe(
                filter((initialized) => initialized === true),
                switchMap(() => from(this.dashboardsService.getSafeCurrentDashboardId())),
            ),
            // React to the user dashboard changed
            this.dashboardsService.currentDashboardChanged$.pipe(filter((id) => !!id)),
            // Disconnect on logout
            this.authService.userLogoutEvent$.pipe(map(() => null as string | null)),
            // Disconnect on logout user switching dashboards
            this.dashboardsService.dashboardChangeEvent$.pipe(map(() => null as string | null)),
            // Disconnect on relogin prompted (false) and restart once relogged (true)
            this.authService.sessionChangedReceivedEvent$.pipe(
                switchMap((sessionChanged) => {
                    if (sessionChanged) {
                        return from(this.dashboardsService.getSafeCurrentDashboardId());
                    }
                    return of(null);
                }),
            ),
        )
            .pipe(distinctUntilChanged())
            .pipe(
                switchMap((id) => (id ? this.connectToHub(id) : EMPTY)),
                takeUntilDestroyed(),
            )
            .subscribe({
                next: (conn) => {
                    this.connection = conn;
                    this.signalRConnected.set(true);
                    this.responseErrorHandlerService.hideBannerEmitter.next({ type: "signalR" });
                },
                complete: () => {
                    this.connection = null;
                    this.signalRConnected.set(false);
                },
            });
    }

    private connectToHub(dashboardId: string): Observable<HubConnection> {
        return new Observable<HubConnection>((subscriber) => {
            let conn: HubConnection | null = null;
            let cancelled = false;

            const start = async () => {
                const { url, accessToken: initialToken } = await firstValueFrom(this.negotiate(dashboardId));
                if (cancelled) return;

                // Use the initial token for the first connect; re-negotiate for subsequent reconnects
                this.firstToken = initialToken;

                conn = new HubConnectionBuilder()
                    .withUrl(url, {
                        // The SignalR client's own internal negotiate/HTTP-transport requests bypass
                        // Angular's HttpClient (and therefore AuthInterceptor), so they never get the
                        // "x-site"/"x-userid"/"x-session-name" headers that every other API call gets.
                        // Attach them the same way AuthService.addSingleSessionHeader() does for
                        // AuthInterceptor, so this app's auth pipeline can authenticate these requests
                        // too (needed for the self-hosted local dev Hub; harmless against Azure SignalR).
                        headers: this.buildSessionHeaders(),
                        accessTokenFactory: async () => {
                            if (this.firstToken !== null) {
                                return this.firstToken;
                            }

                            const { accessToken } = await firstValueFrom(this.negotiate(dashboardId));
                            this.firstToken = accessToken;
                            return accessToken;
                        },
                    })
                    .withAutomaticReconnect()
                    .configureLogging(LogLevel.Information)
                    .build();

                conn.onreconnecting(() => {
                    this.firstToken = null;
                    this.signalRConnected.set(false);
                    this.responseErrorHandlerService.showBannerEmitter.next({ type: "signalR" });
                });

                conn.onreconnected(() => {
                    if (this.connection === conn) {
                        this.signalRConnected.set(true);
                        this.responseErrorHandlerService.hideBannerEmitter.next({ type: "signalR" });
                    }
                });

                // onclose fires on manual stop (teardown) or when withAutomaticReconnect exhausts all retries
                conn.onclose((err) => {
                    if (this.connection === conn) {
                        this.connection = null;
                        this.signalRConnected.set(false);
                    }
                    // If not torn down intentionally by switchMap, propagate error to trigger retry
                    if (!subscriber.closed) {
                        console.error("Real-time updates: Connection lost. Scheduling reconnect.", err);
                        subscriber.error(err ?? new Error("Connection closed unexpectedly"));
                    }
                });

                await conn.start();
                if (cancelled) {
                    conn.stop();
                    return;
                }

                subscriber.next(conn);
            };

            start().catch((err) => {
                if (!subscriber.closed) subscriber.error(err);
            });

            return () => {
                cancelled = true;
                conn?.stop();
            };
        }).pipe(
            retry({
                delay: (err, retryCount) => {
                    // On 401, wait until the user has completed relogin before retrying
                    if (err instanceof HttpErrorResponse && err.status === 401) {
                        return this.authService.sessionChangedReceivedEvent$.pipe(
                            filter((changed) => changed === true),
                            take(1),
                        );
                    }
                    if (retryCount % RETRY_FAST_COUNT === 0) {
                        this.responseErrorHandlerService.showBannerEmitter.next({ type: "signalR" });
                        return timer(RETRY_SLOW_DELAY_MS);
                    }
                    return timer(RETRY_FAST_DELAY_MS);
                },
                resetOnSuccess: true,
            }),
        );
    }

    private negotiate(dashboardId: string) {
        return this.http.get<NegotiateResponse>(this.buildNegotiateUrl(dashboardId), {
            headers: new HttpHeaders().set("showBanner", "false"),
        });
    }

    /** Mirrors AuthService.addSingleSessionHeader() for requests that don't go through AuthInterceptor. */
    private buildSessionHeaders(): Record<string, string> {
        const { site, userId, sessionName } = this.authService.sessionData;
        if (!site || !userId || !sessionName) {
            return {};
        }
        return {
            "x-site": site,
            "x-userid": userId,
            "x-session-name": sessionName,
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy":
                "geolocation=(), midi=(), notifications=(), push=(), sync-xhr=(), microphone=(), camera=(), magnetometer=(), gyroscope=(), speaker=(), vibrate=(), fullscreen=(), payment=()",
        };
    }

    private buildNegotiateUrl(dashboardId: string): string {
        return (
            this.appSettingsService.appSettings.SignalRService_URL +
            this.appSettingsService.appSettings.SignalRService_Version +
            "negotiate/" +
            dashboardId
        );
    }
}
